import i18n from '@/locales/i18n';
import { getExpectedChainId } from '@/utils/wallet-chain';

/**
 * 签名原文里的 URI（后端会校验/按该域重拼 message）。
 * 本地 localhost 若直接写入，验签会报「签名无效」；优先 env，其次本机回落到生产域。
 */
function resolveOrigin() {
  const fromEnv = String(process.env.VUE_APP_WALLET_LOGIN_URI ?? '')
    .trim()
    .replace(/\/$/, '');
  if (fromEnv) return fromEnv;

  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin)) {
      return 'https://deepsage.app';
    }
    return origin;
  }
  return '';
}

function isCanonicalOnlyMode(options = {}) {
  if (options.canonicalOnly === true) return true;
  if (options.canonicalOnly === false) return false;
  return (
    String(process.env.VUE_APP_WALLET_LOGIN_MESSAGE_CANONICAL_ONLY ?? '').toLowerCase() === 'true'
  );
}

function buildCanonicalMessage(nonce, walletAddress) {
  const address = String(walletAddress ?? '').trim();
  const nonceStr = String(nonce ?? '').trim();
  if (!nonceStr || !address) {
    throw new Error('wallet login message fields required');
  }
  return `${nonceStr}\n${address}`;
}

/**
 * 构建钱包登录签名原文（MetaMask 等钱包弹窗展示）。
 * 设置 VUE_APP_WALLET_LOGIN_MESSAGE_CANONICAL_ONLY=true 可回退为仅两行 canonical。
 *
 * @param {string} nonce
 * @param {string} walletAddress
 * @param {{ t?: Function, uri?: string, chainId?: number, issuedAt?: string, canonicalOnly?: boolean }} [options]
 */
export function buildWalletLoginMessage(nonce, walletAddress, options = {}) {
  const canonical = buildCanonicalMessage(nonce, walletAddress);
  if (isCanonicalOnlyMode(options)) {
    return canonical;
  }

  const t = options.t ?? i18n.global.t;
  const uri = options.uri ?? resolveOrigin();
  const chainId = options.chainId ?? getExpectedChainId();
  const issuedAt = options.issuedAt ?? new Date().toISOString();
  const address = String(walletAddress ?? '').trim();
  const nonceStr = String(nonce ?? '').trim();

  return [
    t('authDialog.walletSignMessage.welcome'),
    '',
    t('authDialog.walletSignMessage.identityPrompt', { address }),
    '',
    t('authDialog.walletSignMessage.safety'),
    '',
    `${t('authDialog.walletSignMessage.uriLabel')}: ${uri}`,
    `${t('authDialog.walletSignMessage.versionLabel')}: 1`,
    `${t('authDialog.walletSignMessage.chainIdLabel')}: ${chainId}`,
    `${t('authDialog.walletSignMessage.nonceLabel')}: ${nonceStr}`,
    `${t('authDialog.walletSignMessage.issuedAtLabel')}: ${issuedAt}`,
  ].join('\n');
}

/** 从多语言签名原文的元数据行中提取 nonce（不依赖固定语言标签） */
function extractWalletLoginNonce(lines, address = '') {
  const knownNonceLabels = /^(随机码|Nonce|隨機碼|논스|ノンス|नॉन्स)\s*:/i;
  const labeled = lines.find((line) => knownNonceLabels.test(line));
  if (labeled) {
    return labeled.split(':').slice(1).join(':').trim();
  }

  for (const line of lines) {
    const colon = line.indexOf(':');
    if (colon <= 0) continue;
    const value = line.slice(colon + 1).trim();
    if (!value || value === address) continue;
    if (/^https?:\/\//i.test(value)) continue;
    if (/^\d{1,6}$/.test(value)) continue;
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) continue;
    if (/^[a-z0-9]+$/i.test(value) && value.length >= 8) {
      return value;
    }
  }
  return '';
}

/** 从完整签名原文中提取后端验签用的 canonical 块（nonce + address） */
export function getWalletLoginCanonicalMessage(message) {
  const text = String(message ?? '').trimEnd();
  const lines = text.split('\n').map((line) => line.trim());

  if (lines.length === 2 && lines[0] && lines[1]) {
    return `${lines[0]}\n${lines[1]}`;
  }

  const address = lines.find((line) => /^0x[0-9a-fA-F]{40}$/.test(line)) ?? '';
  const nonce = extractWalletLoginNonce(lines, address);

  if (nonce && address) {
    return `${nonce}\n${address}`;
  }

  return text;
}

export function createWalletLoginNonce() {
  return Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}

export function createWalletLoginSignPayload(walletAddress, options = {}) {
  const nonce = createWalletLoginNonce();
  const message = buildWalletLoginMessage(nonce, walletAddress, options);
  return { nonce, message };
}
